import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import { debounce } from 'lodash';
import { Row, Col, Button, Form, Card, Space, Modal, notification } from 'antd';
import FormInput from '../../../components/FormInput';
import Firebase from '../../../lib/Firebase';
import FormSelect from '../../../components/FormSelect';
import FormTimePicker from '../../../components/FormTimePickerSpanish';
import FormDatePicker from '../../../components/FormDatePicker';
import FormPlacesAutocomplete from '../../../components/FormPlacesAutocomplete';
import { getDistanceBetweenTwoLocation, getAddressDetails } from '../../../lib/googleMapApi';

dayjs.extend(utc);
dayjs.extend(timezone);

const titleStyle = {
	fontFamily: "Roboto",
	fontSize: 20,
	color: "#4A525F",
	fontWeight: 600,
};

const displayNotification = (description, cb = () => {}) => {
	notification.warning({
		message: "Nueva alerta de reserva",
		description,
	});

	cb(false);

	return;
};

const formatAddress = async (address, type = "pickup", cb) => {
	let errorMsg = null;
	let result = {};

	try {
		result = await getAddressDetails(address);
	} catch(err) {
		errorMsg = "Por favor ingrese la dirección correcta.";
	}

	const spanishType = type === "pickup" ? "de recogida" : "de entrega";

	if (result?.addressLine === "") {
		errorMsg = `Por favor ingrese la dirección correcta. Línea de dirección ${spanishType} no válida.`;
	} else if (result?.city === "") {
		errorMsg = `Por favor ingrese la dirección correcta. Ciudad ${spanishType} no válida.`;
	} else if (result?.state === "") {
		errorMsg = `Por favor ingrese la dirección correcta. Estado ${spanishType} no válido.`;
	} else if (result?.zipcode === "") {
		errorMsg = `Por favor ingrese la dirección correcta. Código postal ${spanishType} no válido.`;
	} else if (result?.latitude === 0) {
		errorMsg = `Por favor ingrese la dirección correcta. Valor de latitud ${spanishType} no válido de la dirección.`;
	} else if (result?.longitude === 0) {
		errorMsg = `Por favor ingrese la dirección correcta. Valor de longitud ${spanishType} no válido de la dirección.`;
	} else {
		return result;
	}


	displayNotification(errorMsg, cb);

	return null;
}

function PartnershipForChildrenPortalSpanish({
	fleetData,
	facilityId,
	portalId,
	spanWindow
}) {
	const [form] = Form.useForm();
	const [tripFrom, setTripFrom] = useState("Somebody else");
	const [tripLeg, setTripLeg] = useState(1);
	const [isFieldReset, setIsFieldReset] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const programTimezone = fleetData?.program?.timezone || null
	const mobilityAssistanceList = fleetData?.mobility_assistance || [];
	const paymentMethodList = facilityId ? [
		// this is in alphabetical order
		// do not interchange
		{value: 1, label: `Bill to ${fleetData?.facility?.name}`},
		{value: 2, label: "Credit card or debit card"},
		{value: 0, label: "Cash or check"},
	] : [
		{value: 2, label: "Credit card or debit card"},
		{value: 0, label: "Cash or check"},
	];

	// watched forms
	const userFirstname = Form.useWatch('user_firstname', form);
	const userLastname = Form.useWatch('user_lastname', form);
	const userPhone = Form.useWatch('user_phone', form);
	const userEmail = Form.useWatch('user_email', form);

	const onFinish = debounce(async (values) => {
		try {
			setIsSubmitting(true);

			const {
				dropoff_address,
				pickup_address,
				user_phone,
				rider_phone,
			} = values;

			if (user_phone && user_phone.length !== 14) {
				displayNotification("El formato del teléfono del cliente no es válido.", setIsSubmitting);

				return;
			}

			if (rider_phone && rider_phone.length !== 14) {
				displayNotification("El formato del teléfono del pasajero no es válido.", setIsSubmitting);

				return;
			}

			if (pickup_address === dropoff_address) {
				displayNotification("Las direcciones de recogida y devolución no pueden ser las mismas. Por favor corrija esto.", setIsSubmitting);

				return;
			}

			const formattedPickupAddress = await formatAddress(pickup_address, "pickup", setIsSubmitting);
			const formattedDropoffAddress = formattedPickupAddress && await formatAddress(dropoff_address, "dropoff", setIsSubmitting);

			let meters = 0;
			let duration = 0;

			try {
				// get travel distance and time
				const origin = pickup_address;
				const destination = dropoff_address;

				const routesBetween = await getDistanceBetweenTwoLocation(
					origin,
					destination
				);

				meters = routesBetween.rows[0].elements[0].distance.value;
				duration = routesBetween.rows[0].elements[0].duration.value;
			} catch (error) {
				displayNotification("Por favor ingrese la dirección correcta!", setIsSubmitting);

				return;
			}

			if (formattedPickupAddress && formattedDropoffAddress) {
				values.public_portal_id = portalId;
				values.travel_distance = meters;
				values.travel_time = duration;
				values.pickup_address = formattedPickupAddress;
				values.dropoff_address = formattedDropoffAddress;
				values.program_id = fleetData.program.id;
				values.fleet_id = fleetData.id;
				values.pickup_date = values.pickup_date && dayjs(values.pickup_date).format("dddd, MMMM D, YYYY");
				values.return_date = values.return_date && dayjs(values.return_date).format("dddd, MMMM D, YYYY");
				values.pickup_time = values.pickup_time === "Will call" ? "11:59 PM" : values.pickup_time;
				values.return_time = values.return_time === "Will call" ? "11:59 PM" : values.return_time;
				values.appointment_date = values.pickup_date;
				values.appointment_time = values.appointment_time === "Will call" ? "11:59 PM" : values.appointment_time;
				values.mobility_assistance = values.mobility_assistance === "None"
					? [] : [values.mobility_assistance];
				values.office_note =
					"Submitted through Portal by:\n"
					+ `First Name: ${(values.user_firstname || "").trim()}\n`
					+ `Last Name: ${(values.user_lastname || "").trim()}\n`
					+ `Mobile Phone: ${values.user_phone}\n`
					+ `Email: ${values.user_email}\n`
					+ `Preferred contact language: ${values.preferred_contact_language}\n`
					+ "\n"
					+ "Rider information:\n"
					+ `First Name: ${(values.rider_firstname || "").trim()}\n`
					+ `Last Name: ${(values.rider_lastname || "").trim()}\n`
					+ `Mobile Phone: ${values.rider_phone || ""}\n`
					+ `Email: ${values.rider_email || ""}\n`
					+ "\n"
					+ `Car seat lbs: ${values.car_seat_lbs}\n`
					+ `Are you flexible with pickup time: ${values.is_pickup_time_flexible || false}\n`
					+ `${values.appointment_time ? `Appointment Time: ${values.appointment_time || ""}\n` : ""}`
					+ "\n"
					+ `Have you used our services before: ${values.has_already_used_service || false}\n`
					+ `Notes: ${values.office_note || ""}\n`
				;

				if (facilityId) {
					values.facility_portal_id = facilityId;

					if (!values?.payment_method) {
						values.payment_method = 1;
					}

					if (values.payment_method === 1) {
						values.insurance_id = fleetData?.facility.id;
					}
				}

				const functionName = "v1_webapp_public_create_new_reservation";
				const appCheckToken = await Firebase.getAppCheckToken();

				await axios.post(`${process.env.REACT_APP_DUET_SERVER_URL}/${functionName}`, {
					...values
				}, {
					headers: {
						'X-Parse-Application-Id': process.env.REACT_APP_DUET_APP_ID,
						'X-Firebase-AppCheck': appCheckToken,
						'Content-Type': 'application/json'
					},
				});

				form.resetFields();
				setIsFieldReset(true);
				setIsSubmitting(false);
				setTripFrom("Somebody else");

				notification.success({
					message: "Nueva reserva exitosa",
					description: 'La reserva se creó con éxito.',
				});
			}
		} catch (error) {
			notification.error({
				message: "Error de nueva reserva",
				description: error?.response?.data
				? error.response.data.error.message
				: error.toString(),
			});
			setIsSubmitting(false);
		}
	}, 1000);
	
	const onValuesChange = (data) => {
		const { trip_from, trip_leg } = data;

		if (trip_from) {
			setTripFrom(trip_from);
		}

		if (trip_leg) {
			setTripLeg(trip_leg);
		}
	};

	const cancelModal = () => {
		Modal.confirm({
		  title: '¿Quieres cancelar esta reserva?',
		  content: (
			<p>Todos los datos ingresados ​​en los campos se restablecerán.</p>
		  ),
		  onOk: () => {
			form.resetFields();
			setIsFieldReset(true);
			setTripFrom("Somebody else");
		  },
		});
	};

	useEffect(() => {
		if (tripFrom === "Myself" && !isSubmitting) {
			if (userFirstname
				|| userLastname
				|| userPhone
				|| userEmail
				|| userFirstname === ""
				|| userLastname === ""
				|| userPhone === ""
				|| userEmail === ""
			) {
				form.setFieldsValue({
					rider_firstname: userFirstname,
					rider_lastname: userLastname,
					rider_phone: userPhone,
					rider_email: userEmail,
				});
			}
		}
	}, [form, tripFrom, userFirstname, userLastname, userPhone, userEmail, isSubmitting])

	useEffect(() => {
		if (tripFrom === "Somebody else") {
			form.setFieldsValue({
				rider_firstname: null,
				rider_lastname: null,
				rider_phone: null,
				rider_email: null,
			});
		}
	}, [form, tripFrom]);

	useEffect(() => {
		if (tripLeg === 1) {
			form.setFieldsValue({
				return_date: null,
				return_time: null,
			})
		}
	}, [tripLeg, form]);

	return (
		<div
			style={{
				textAlign: "left",
				fontFamily: "Roboto",
			}}
		>
			<div className="fleet-title">
				<span
					style={{
						display: "block",
						fontSize: 26,
						fontWeight: 800,
						marginBottom: 20,
						lineHeight: "normal"
					}}
				>
					{fleetData?.name}
				</span>
				<span
					style={{
						display: `${(fleetData?.phone || fleetData?.email) ? "inline-block" : "none"}`,
						fontSize: 18,
						fontWeight: 400,
						lineHeight: 1.5
					}}
				>
					{fleetData?.phone ? `Phone: ${fleetData?.phone}${fleetData?.email ? "," : ""}` : ""} {fleetData?.email ? `Email: ${fleetData?.email}`: ""}
				</span>
			</div>
			<Card
				title={
					<Row style={{margin: "20px 0"}}>
						<Col span={24} style={{marginBottom: 5}}>
							<span style={{...titleStyle, fontWeight: 700}}>Consulta o Reserve un Viaje</span>
						</Col>
						<Col span={24} style={{fontSize: 14, display: "contents"}}>
							<span style={{color: "#9398A0"}}>
								Utilice este formulario para enviar su consulta de viaje.
								Nos comunicaremos con usted en breve.
							</span>
						</Col>
					</Row>
				}
			>
				<Form
					form={form}
					name="reservation-form"
					onFinish={onFinish}
					onValuesChange={onValuesChange}
					autoComplete="off"
				>
					<div style={{marginBottom: 20}}>
						<span style={{ ...titleStyle, fontSize: 18 }}>
							Su información de contacto
						</span>
						<Card
							size="small"
							className="description-container"
						>
							<Row gutter={[20, 0]}>
								<Col span={spanWindow}>
									<FormInput
										name="user_firstname"
										label="Nombre"
										rules={[{
											pattern: /^[A-Za-z\s]*$/,
											message: "Por favor ingrese solo letras."
										}]}
										required
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										name="user_lastname"
										label="Apellido"
										rules={[{
											pattern: /^[A-Za-z\s]*$/,
											message: "Por favor ingrese solo letras."
										}]}
										required
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										type="phone"
										name="user_phone"
										label="Numero de Telefono"
										setValue={(data) => form.setFieldValue("user_phone", data)}
										rules={[{
											pattern: /^[0-9()-+-\s]+$/,
											message: "Por favor introduzca un número de teléfono móvil válido."
										}]}
										required
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										name="user_email"
										label="Correo Electrónico"
										rules={[{
											type: "email",
											message: "Por favor ingrese una dirección de correo electrónico válida.",
										}]}
									/>
								</Col>
								<Col span={24}>
									<FormSelect
										name="trip_from"
										label="¿Para quién es este viaje?"
										options={[
											{ value: "Myself", label: "Mí mismo" },
											{ value: "Somebody else", label: "Algún otro" },
										]}
										required
									/>
								</Col>
								<Col span={24}>
									<FormSelect
										name="preferred_contact_language"
										label="Idioma de contacto preferido"
										options={[
											{ value: "English", label: "Inglés" },
											{ value: "Spanish", label: "Español" },
										]}
										required
									/>
								</Col>
							</Row>
						</Card>
					</div>
					<div style={{marginBottom: 20}}>
						<span style={{ ...titleStyle, fontSize: 18 }}>
							Pasajero
						</span>
						<Card
							size="small"
							className="description-container"
						>
							<Row gutter={[20, 0]}>
								<Col span={spanWindow}>
									<FormInput
										name="rider_firstname"
										label="Nombre"
										rules={[{
											pattern: /^[A-Za-z\s]*$/,
											message: "Por favor ingrese solo letras."
										}]}
										required
										disabled={tripFrom === "Myself"}
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										name="rider_lastname"
										label="Apellido"
										rules={[{
											pattern: /^[A-Za-z\s]*$/,
											message: "Por favor ingrese solo letras."
										}]}
										required
										disabled={tripFrom === "Myself"}
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										type="phone"
										name="rider_phone"
										label="Numero de Telefono"
										setValue={(data) => form.setFieldValue("rider_phone", data)}
										rules={[{
											pattern: /^[0-9()-+-\s]+$/,
											message: "Por favor introduzca un número de teléfono móvil válido."
										}]}
										disabled={tripFrom === "Myself"}
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										name="rider_email"
										label="Correo Electrónico"
										rules={[{
											type: "email",
											message: "Por favor ingrese una dirección de correo electrónico válida.",
										}]}
										disabled={tripFrom === "Myself"}
									/>
								</Col>
							</Row>
						</Card>
					</div>
					<div style={{marginBottom: 40}}>
						<span style={{ ...titleStyle, fontSize: 18 }}>
							Informacion del viaje
						</span>
						<Card
							size="small"
							className="description-container"
						>
							<Row gutter={[20, 0]}>
								<Col span={spanWindow}>
									<FormDatePicker
										name="pickup_date"
										label="Fecha de Recogida"
										disabledDate={(current) => {
											const currentDate = dayjs(current).tz(programTimezone);
											return  currentDate.isBefore(dayjs.tz(new Date(), programTimezone).startOf("day"));
										}}
									/>
								</Col>
								<Col span={spanWindow}>
									<FormTimePicker
										name="pickup_time"
										label="Hora de Recogida"
										selectedDate={() => form.getFieldValue("pickup_date")}
										selectedTime={() => form.getFieldValue("pickup_time")}
										timezone={programTimezone}
										isReset={isFieldReset}
										setIsReset={setIsFieldReset}
										setValue={(data) => form.setFieldValue("pickup_time", data)}
									/>
								</Col>
								<Col span={24}>
									<FormTimePicker
										name="appointment_time"
										label="Cita o Hora de Llegada (si aplica)"
										selectedTime={() => form.getFieldValue("appointment_time")}
										timezone={programTimezone}
										isReset={isFieldReset}
										setIsReset={setIsFieldReset}
										setValue={(data) => form.setFieldValue("appointment_time", data)}
									/>
								</Col>
								<Col span={spanWindow}>
									<FormSelect
										name="mobility_assistance"
										label="Asistencia de Movilidad"
										options={[{
											value: "None",
											label: "Ninguno"
										}].concat(mobilityAssistanceList
											.map((ma) => {
												// Translate label to spanish
												let label = "";

												switch(ma.label) {
												case "Ambulatory":
													label = "Ambulatorio/a";
													break;
												case "Wheelchair":
													label = "Silla De Ruedas";
													break;
												case "Stretcher":
													label = "Camilla";
													break;
												case "Door to door":
													label = "De Puerta a Puerta";
													break;
												case "Cane or walker":
													label = "Baston o Andadedra";
													break;
												case "Car seat":
													label = "Asiento de Caro";
													break;
												case "Pregnant":
													label = "Embarazada";
													break;
												case "Crutches":
													label = "Muletas";
													break;
												case "Others":
													label = "Otro/a";
													break;
												default:
													// Do Nothing
												}

												return {
													value: ma.type,
													label,
												};
											})
											.sort((a, b) => a.value - b.value))}
										required
									/>
								</Col>
								<Col span={spanWindow}>
									<FormSelect
										name="additional_passenger"
										label="Pasajero Adicional"
										options={[
											{value: 0, label: "Ninguno"},
											{value: 1, label: "1"},
											{value: 2, label: "2"},
											{value: 3, label: "3"},
										]}
										required
									/>
								</Col>
								<Col span={24}>
									<FormPlacesAutocomplete
										name="pickup_address"
										label="Dirección de Recogida"
										isReset={isFieldReset}
										setIsReset={setIsFieldReset}
										setValue={(data) => form.setFieldValue("pickup_address", data)}
										required
									/>
								</Col>
								<Col span={24}>
									<FormPlacesAutocomplete
										name="dropoff_address"
										label="Dirección de Destino"
										isReset={isFieldReset}
										setIsReset={setIsFieldReset}
										setValue={(data) => form.setFieldValue("dropoff_address", data)}
										required
									/>
								</Col>
								<Col span={24}>
									<FormSelect
										name="trip_leg"
										label="¿Solo Ida o Viaje Redondo?"
										options={[
											{value: 1, label: "Solo ida"},
											{value: 2, label: "Viaje redondo"},
										]}
										required
									/>
								</Col>
								<Col span={spanWindow}>
									<FormDatePicker
										name="return_date"
										label="Fecha de Regreso"
										disabled={tripLeg === 1}
										disabledDate={(current) => {
											const currentDate = dayjs(current).tz(programTimezone);
											return  currentDate.isBefore(dayjs.tz(new Date(), programTimezone).startOf("day"));
										}}
									/>
								</Col>
								<Col span={spanWindow}>
									<FormTimePicker
										name="return_time"
										label="Hora de Regreso"
										disabled={tripLeg === 1}
										selectedDate={() => form.getFieldValue("return_date")}
										selectedTime={() => form.getFieldValue("return_time")}
										timezone={programTimezone}
										isReset={isFieldReset}
										setIsReset={setIsFieldReset}
										setValue={(data) => form.setFieldValue("return_time", data)}
									/>
								</Col>
								{!facilityId && <Col span={spanWindow}>
									<FormSelect
										name="payment_method"
										label="¿Cómo se paga el viaje?"
										options={paymentMethodList}
										required
									/>
								</Col>}
								<Col span={spanWindow}>
									<FormInput
										name="car_seat_lbs"
										label="¿Necesita asiento para auto? Si es asi, indique el peso (lbs)"
									/>
								</Col>
								<Col span={spanWindow}>
									<FormSelect
										name="is_pickup_time_flexible"
										label="¿Es flexible con la hora de recogida?"
										options={[
											{value: "Yes", label: "Si"},
											{value: "No", label: "No"},
										]}
										required
									/>
								</Col>
								<Col span={24}>
									<FormSelect
										name="has_already_used_service"
										label="¿Ha utilizado nuestros servicios antes?"
										options={[
											{value: "Yes", label: "Si"},
											{value: "No", label: "No"},
										]}
										required
									/>
								</Col>
								<Col span={24}>
									<FormInput
										type="text-area"
										name="office_note"
										label="Anote el motivo de la cita médica"
									/>
								</Col>
							</Row>
						</Card>
					</div>
					<Form.Item
						style={{
							textAlign: "right",
							marginBottom: 26,
						}}
					>
						<Space>
							<Button
								style={{
									borderRadius: 3,
									fontWeight: 600,
									backgroundColor: "#D7DCE5",
									color: "#4A525F"
								}}
								size="large"
								htmlType="button"
								onClick={cancelModal}
							>
								Cancelar
							</Button>
							<Button
								style={{
									backgroundColor: "#118BF0",
									color: "#FFFFFF",
									borderRadius: 3,
									fontWeight: 600,
								}}
								size="large"
								htmlType="submit"
								loading={isSubmitting}
							>
								Eviar
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Card>
		</div>
	);
}

export default PartnershipForChildrenPortalSpanish;